import { useState, useEffect } from "react";
import { useSearch, useLocation } from "wouter";
import { emitTourEvent } from "@/lib/tour-events";
import { usePageTitle } from "@/hooks/use-page-title";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useSettingsFormat } from "@/hooks/use-settings-format";
import {
  useListProducts,
  useCreateProduct,
  useUpdateProduct,
  useDeleteProduct,
  useBulkDeleteProducts,
  useBulkUpdateProductCategory,
  getListProductsQueryKey,
  useListProductDefaultCosts,
  useCreateProductDefaultCost,
  useUpdateProductDefaultCost,
  useDeleteProductDefaultCost,
  getListProductDefaultCostsQueryKey,
  useListInternalCatalog,
} from "@workspace/api-client-react";
import type { Product, ProductDefaultCost } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Package, Plus, Search, Pencil, Trash2, Loader2,
  DollarSign, Tag, Layers, EyeOff, Edit, Save, ChevronRight, Hash,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ComboboxInput } from "@/components/ui/combobox-input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription,
} from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { BulkDeleteDialog } from "@/components/bulk-delete-dialog";
import { ProGate } from "@/components/pro-gate";

const productSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  unitPrice: z.coerce.number().min(0, "Price must be 0 or more").max(99999999.99, "Maximum value is 99,999,999"),
  unit: z.string().optional(),
  category: z.string().optional(),
  sku: z.string().optional(),
  taxable: z.boolean().optional(),
  productType: z.enum(["service", "non_inventory"]).optional(),
});
type ProductForm = z.infer<typeof productSchema>;

const defaultCostSchema = z.object({
  catalogItemId: z.coerce.number().nullable().optional(),
  name: z.string().min(1, "Required"),
  quantity: z.coerce.number().min(0.001),
  unit: z.string().optional(),
  unitCost: z.coerce.number().min(0),
  supplier: z.string().optional(),
  notes: z.string().optional(),
});
type DefaultCostForm = z.infer<typeof defaultCostSchema>;

const UNITS = ["each", "hour", "sqft", "lf", "day", "lb", "ton", "yd", "gal"];

export default function Products() {
  usePageTitle("Products & Services");
  const { toast } = useToast();
  const { formatCurrency } = useSettingsFormat();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [openCategories, setOpenCategories] = useState<Set<string>>(new Set());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editProduct, setEditProduct] = useState<Product | null>(null);
  const [justCreatedProductId, setJustCreatedProductId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Product | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkCategoryOpen, setBulkCategoryOpen] = useState(false);
  const [bulkCategoryTarget, setBulkCategoryTarget] = useState("");
  const searchString = useSearch();
  const [, navigate] = useLocation();

  useEffect(() => {
    if (new URLSearchParams(searchString).get("new") === "1") {
      setDialogOpen(true);
      navigate("/products", { replace: true });
    }
  }, [searchString, navigate]);

  const debouncedSearch = useDebouncedValue(search);
  const { data: products, isLoading } = useListProducts(
    debouncedSearch ? { search: debouncedSearch } : {},
    { query: { queryKey: getListProductsQueryKey(debouncedSearch ? { search: debouncedSearch } : {}) } }
  );

  const createProduct = useCreateProduct();
  const updateProduct = useUpdateProduct();
  const deleteProduct = useDeleteProduct();
  const bulkDeleteProducts = useBulkDeleteProducts();
  const bulkUpdateProductCategory = useBulkUpdateProductCategory();

  const form = useForm<ProductForm>({
    resolver: zodResolver(productSchema),
    defaultValues: { name: "", description: "", unitPrice: 0, unit: "each", category: "", sku: "", taxable: true, productType: "service" },
  });

  const openCreate = () => {
    setEditProduct(null);
    setJustCreatedProductId(null);
    form.reset({ name: "", description: "", unitPrice: 0, unit: "each", category: "", sku: "", taxable: true, productType: "service" });
    setDialogOpen(true);
  };

  const openEdit = (product: Product) => {
    setEditProduct(product);
    setJustCreatedProductId(null);
    form.reset({
      name: product.name,
      description: product.description ?? "",
      unitPrice: product.unitPrice,
      unit: product.unit ?? "each",
      category: product.category ?? "",
      sku: product.sku ?? "",
      taxable: product.taxable ?? true,
      productType: (product.productType as "service" | "non_inventory") ?? "service",
    });
    setDialogOpen(true);
  };

  const onSubmit = (data: ProductForm) => {
    const payload = {
      name: data.name,
      description: data.description || undefined,
      unitPrice: data.unitPrice,
      unit: data.unit || undefined,
      category: data.category || undefined,
      sku: data.sku || undefined,
      taxable: data.taxable,
      productType: data.productType,
    };

    if (editProduct) {
      updateProduct.mutate({ id: editProduct.id, data: payload }, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListProductsQueryKey() });
          setDialogOpen(false);
          toast({ title: "Product updated" });
        },
        onError: () => toast({ title: "Failed to update", variant: "destructive" }),
      });
    } else {
      createProduct.mutate({ data: payload }, {
        onSuccess: (newProduct) => {
          queryClient.invalidateQueries({ queryKey: getListProductsQueryKey() });
          setJustCreatedProductId(newProduct.id);
          emitTourEvent("product.created");
          toast({ title: "Product created — add costs below" });
        },
        onError: () => toast({ title: "Failed to create", variant: "destructive" }),
      });
    }
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    deleteProduct.mutate({ id: deleteTarget.id }, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListProductsQueryKey() });
        setDeleteTarget(null);
        toast({ title: "Product deleted" });
      },
      onError: () => toast({ title: "Failed to delete", variant: "destructive" }),
    });
  };

  const allProductIds = products?.map(p => p.id) ?? [];
  const allSelected = allProductIds.length > 0 && allProductIds.every(id => selectedIds.has(id));
  const someSelected = allProductIds.some(id => selectedIds.has(id)) && !allSelected;

  const toggleSelectAll = () => {
    if (allSelected || someSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allProductIds));
    }
  };

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const confirmBulkCategory = () => {
    const ids = Array.from(selectedIds);
    bulkUpdateProductCategory.mutate({ data: { ids, category: bulkCategoryTarget } }, {
      onSuccess: (result) => {
        queryClient.invalidateQueries({ queryKey: getListProductsQueryKey() });
        setSelectedIds(new Set());
        setBulkCategoryOpen(false);
        setBulkCategoryTarget("");
        toast({ title: `${result.updated} product(s) moved to "${bulkCategoryTarget || "Uncategorized"}"` });
      },
      onError: () => {
        setBulkCategoryOpen(false);
        toast({ title: "Failed to update category", variant: "destructive" });
      },
    });
  };

  const confirmBulkDelete = () => {
    const ids = Array.from(selectedIds);
    bulkDeleteProducts.mutate({ data: { ids } }, {
      onSuccess: (result) => {
        queryClient.invalidateQueries({ queryKey: getListProductsQueryKey() });
        setSelectedIds(new Set());
        setBulkDeleteOpen(false);
        toast({ title: `${result.deleted} product(s) deleted` });
      },
      onError: (err: unknown) => {
        setBulkDeleteOpen(false);
        const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
          ?? "Failed to delete selected products";
        toast({ title: "Could not delete", description: message, variant: "destructive" });
      },
    });
  };

  const grouped = products?.reduce((acc, p) => {
    const cat = p.category || "Uncategorized";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(p);
    return acc;
  }, {} as Record<string, Product[]>);

  const categoryOptions = Array.from(
    new Set((products ?? []).map((p) => p.category).filter(Boolean) as string[])
  ).sort();

  const isPending = createProduct.isPending || updateProduct.isPending;

  const toggleCategory = (cat: string) => {
    setOpenCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) {
        next.delete(cat);
      } else {
        next.add(cat);
      }
      return next;
    });
  };

  const selectedCount = selectedIds.size;

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Package className="w-8 h-8 text-primary" />
            Products & Services
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Your catalog of items you can add to quotes.
          </p>
        </div>
        <Button onClick={openCreate} className="gap-2 shrink-0">
          <Plus className="w-4 h-4" />
          New Product
        </Button>
      </div>

      {/* Search */}
      <div className="flex items-center gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search products..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        {products && products.length > 0 && (
          <div className="flex items-center gap-2">
            <Checkbox
              checked={allSelected ? true : someSelected ? "indeterminate" : false}
              onCheckedChange={toggleSelectAll}
              aria-label="Select all products"
            />
            <span className="text-sm text-muted-foreground">Select all</span>
          </div>
        )}
      </div>

      {/* Bulk action bar */}
      {selectedCount > 0 && (
        <div className="flex items-center gap-3 flex-wrap px-4 py-2.5 bg-primary/5 border border-primary/20 rounded-lg">
          <span className="text-sm font-medium">{selectedCount} selected</span>
          <Button
            size="sm"
            variant="outline"
            className="gap-2 h-8"
            onClick={() => { setBulkCategoryTarget(""); setBulkCategoryOpen(true); }}
          >
            <Tag className="w-3.5 h-3.5" />
            Change Category
          </Button>
          <Button
            size="sm"
            variant="destructive"
            className="gap-2 h-8"
            onClick={() => setBulkDeleteOpen(true)}
          >
            <Trash2 className="w-3.5 h-3.5" />
            Delete selected ({selectedCount})
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-8"
            onClick={() => setSelectedIds(new Set())}
          >
            Clear selection
          </Button>
        </div>
      )}

      {/* Content */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      ) : !products?.length ? (
        <div className="flex flex-col items-center justify-center py-20 border-2 border-dashed rounded-xl text-center">
          <Package className="w-12 h-12 text-muted-foreground/30 mb-4" />
          <p className="text-lg font-medium">No products yet</p>
          <p className="text-sm text-muted-foreground mt-1 mb-4">
            Add your common services and materials to speed up quotes.
          </p>
          <Button onClick={openCreate} variant="outline" className="gap-2">
            <Plus className="w-4 h-4" />
            Add First Product
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {Object.entries(grouped ?? {}).sort(([a], [b]) => a.localeCompare(b)).map(([category, items]) => {
            const isOpen = openCategories.has(category);
            const categoryIds = items.map(p => p.id);
            const allCatSelected = categoryIds.every(id => selectedIds.has(id));
            const someCatSelected = categoryIds.some(id => selectedIds.has(id)) && !allCatSelected;
            return (
              <div key={category} className="border border-border/50 rounded-lg overflow-hidden bg-card">
                <div className="flex items-center bg-muted/30 hover:bg-muted/50 transition-colors">
                  <div className="pl-3 pr-1 py-3" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={allCatSelected ? true : someCatSelected ? "indeterminate" : false}
                      onCheckedChange={() => {
                        setSelectedIds(prev => {
                          const next = new Set(prev);
                          if (allCatSelected || someCatSelected) {
                            categoryIds.forEach(id => next.delete(id));
                          } else {
                            categoryIds.forEach(id => next.add(id));
                          }
                          return next;
                        });
                      }}
                      aria-label={`Select all in ${category}`}
                    />
                  </div>
                  <button
                    type="button"
                    className="flex-1 flex items-center gap-2 px-2 py-3 text-left"
                    onClick={() => toggleCategory(category)}
                  >
                    <ChevronRight
                      className={cn(
                        "w-4 h-4 text-muted-foreground transition-transform duration-200 shrink-0",
                        isOpen && "rotate-90"
                      )}
                    />
                    <Layers className="w-4 h-4 text-muted-foreground shrink-0" />
                    <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex-1">
                      {category}
                    </h2>
                    <span className="text-xs text-muted-foreground shrink-0 pr-2">
                      {items.length} {items.length === 1 ? "item" : "items"}
                    </span>
                  </button>
                </div>

                {isOpen && (
                  <div className="p-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {items.map((product) => {
                      const isSelected = selectedIds.has(product.id);
                      return (
                        <Card
                          key={product.id}
                          className={cn(
                            "group hover:shadow-md transition-shadow border-border/50 cursor-pointer relative",
                            isSelected && "ring-2 ring-primary/50 border-primary/30"
                          )}
                          onClick={() => navigate(`/products/${product.id}`)}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between gap-2">
                              <div
                                className="pt-0.5 shrink-0"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={() => toggleSelect(product.id)}
                                  aria-label={`Select ${product.name}`}
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-foreground truncate group-hover:text-primary transition-colors">{product.name}</p>
                                {product.description && (
                                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{product.description}</p>
                                )}
                              </div>
                              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); openEdit(product); }}>
                                  <Pencil className="w-3.5 h-3.5" />
                                </Button>
                                <Button
                                  variant="ghost" size="icon"
                                  className="h-7 w-7 text-destructive hover:text-destructive hover:bg-destructive/10"
                                  onClick={(e) => { e.stopPropagation(); setDeleteTarget(product); }}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 mt-3">
                              <span className="text-lg font-bold text-primary">
                                {formatCurrency(Number(product.unitPrice))}
                              </span>
                              {product.unit && (
                                <Badge variant="secondary" className="text-xs">
                                  per {product.unit}
                                </Badge>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) { setJustCreatedProductId(null); form.reset(); } setDialogOpen(open); }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editProduct ? "Edit Product" : justCreatedProductId ? "Edit Product" : "New Product / Service"}
            </DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name *</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. Framing Labor, Drywall Installation" {...field} disabled={!!justCreatedProductId} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Default Description</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Optional description shown on quotes by default"
                        className="min-h-[72px] resize-none"
                        {...field}
                        disabled={!!justCreatedProductId}
                      />
                    </FormControl>
                    <FormDescription>Pre-fills the description when added to a quote.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="unitPrice"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-1.5">
                        <DollarSign className="w-3.5 h-3.5 text-muted-foreground" />
                        Unit Price *
                      </FormLabel>
                      <FormControl>
                        <Input type="number" step="0.01" min="0" placeholder="0.00" {...field} disabled={!!justCreatedProductId} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="unit"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Unit</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="each"
                          list="unit-options"
                          {...field}
                          disabled={!!justCreatedProductId}
                        />
                      </FormControl>
                      <datalist id="unit-options">
                        {UNITS.map(u => <option key={u} value={u} />)}
                      </datalist>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-1.5">
                      <Tag className="w-3.5 h-3.5 text-muted-foreground" />
                      Category
                    </FormLabel>
                    <FormControl>
                      <ComboboxInput
                        value={field.value ?? ""}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        suggestions={categoryOptions}
                        placeholder="e.g. Labor, Materials, Equipment"
                        disabled={!!justCreatedProductId}
                      />
                    </FormControl>
                    <FormDescription>Groups products together in your catalog.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="sku"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-1.5">
                      <Hash className="w-3.5 h-3.5 text-muted-foreground" />
                      SKU
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. SVC-001" {...field} disabled={!!justCreatedProductId} />
                    </FormControl>
                    <FormDescription>Optional unique identifier for this product.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="productType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Type</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value ?? "service"} disabled={!!justCreatedProductId}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="service">Service</SelectItem>
                          <SelectItem value="non_inventory">Non-Inventory</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="taxable"
                  render={({ field }) => (
                    <FormItem className="flex flex-col justify-end pb-1">
                      <FormLabel>Taxable</FormLabel>
                      <div className="flex items-center gap-2 mt-1">
                        <Switch
                          checked={field.value ?? true}
                          onCheckedChange={field.onChange}
                          disabled={!!justCreatedProductId}
                        />
                        <span className="text-sm text-muted-foreground">{field.value ?? true ? "Yes" : "No"}</span>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {!justCreatedProductId && (
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isPending}>
                    {isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : editProduct ? "Save Changes" : "Create Product"}
                  </Button>
                </DialogFooter>
              )}
            </form>
          </Form>

          {/* Default Costs panel - shown when editing or after a new product is created */}
          {(editProduct || justCreatedProductId) && (
            <>
              {justCreatedProductId && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-green-50 border border-green-200 text-green-800 text-sm mt-2">
                  <Package className="w-4 h-4 shrink-0 text-green-600" />
                  <span>Product created — add default costs below, then click Done.</span>
                </div>
              )}
              <ProGate feature="PRODUCT_COST_ADD" mode="overlay">
                <DefaultCostsPanel productId={editProduct ? editProduct.id : justCreatedProductId!} />
              </ProGate>
            </>
          )}

          {justCreatedProductId && (
            <DialogFooter className="mt-4">
              <Button onClick={() => { setJustCreatedProductId(null); form.reset(); setDialogOpen(false); }}>
                Done
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirm (single) */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the product from your catalog. Existing line items that used this product won't be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirm */}
      <BulkDeleteDialog
        open={bulkDeleteOpen}
        onOpenChange={setBulkDeleteOpen}
        count={selectedCount}
        itemLabel="product"
        onConfirm={confirmBulkDelete}
        isPending={bulkDeleteProducts.isPending}
      />

      {/* Bulk Change Category Dialog */}
      <Dialog open={bulkCategoryOpen} onOpenChange={(open) => { setBulkCategoryOpen(open); if (!open) setBulkCategoryTarget(""); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Change Category for {selectedCount} Product{selectedCount !== 1 ? "s" : ""}</DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-2">
            <p className="text-sm text-muted-foreground">Select an existing category or type a new one.</p>
            <ComboboxInput
              value={bulkCategoryTarget}
              onChange={setBulkCategoryTarget}
              suggestions={categoryOptions}
              placeholder="e.g. Roofing, Electrical, Plumbing"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkCategoryOpen(false)}>Cancel</Button>
            <Button onClick={confirmBulkCategory} disabled={bulkUpdateProductCategory.isPending}>
              {bulkUpdateProductCategory.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DefaultCostsPanel({ productId }: { productId: number }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { formatCurrency } = useSettingsFormat();
  const [addOpen, setAddOpen] = useState(false);
  const [editingCost, setEditingCost] = useState<ProductDefaultCost | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ProductDefaultCost | null>(null);

  const { data: costs, isLoading } = useListProductDefaultCosts(productId, {
    query: { queryKey: getListProductDefaultCostsQueryKey(productId) }
  });

  const deleteCost = useDeleteProductDefaultCost();

  const onDeleteCost = (costId: number) => {
    deleteCost.mutate({ productId, costId }, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListProductDefaultCostsQueryKey(productId) });
        setDeleteTarget(null);
        toast({ title: "Default cost removed" });
      },
      onError: () => toast({ title: "Failed to delete cost", variant: "destructive" }),
    });
  };

  const totalCost = costs?.reduce((s, c) => s + c.totalCost, 0) ?? 0;

  return (
    <div className="mt-4 pt-4 border-t">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <EyeOff className="w-4 h-4 text-primary" />
          <span className="text-sm font-semibold">Default Costs</span>
          <Badge variant="outline" className="text-[10px] uppercase tracking-wider text-muted-foreground">Private</Badge>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7 text-xs gap-1"
          onClick={() => { setEditingCost(null); setAddOpen(true); }}
        >
          <Plus className="w-3.5 h-3.5" /> Add Cost
        </Button>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        These costs are automatically added to line items when this product is selected on a quote or invoice.
      </p>

      {isLoading && <div className="text-xs text-muted-foreground py-2">Loading...</div>}

      {!isLoading && (!costs || costs.length === 0) && (
        <div className="text-xs text-muted-foreground py-3 text-center border border-dashed border-border rounded-md bg-muted/20">
          No default costs set. Add costs to auto-populate quotes.
        </div>
      )}

      {costs && costs.length > 0 && (
        <div className="border rounded-md divide-y bg-background">
          {costs.map(cost => (
            <div key={cost.id} className="p-2.5 text-sm hover:bg-muted/20 group">
              <div className="flex justify-between items-center">
                <span className="font-medium text-sm">{cost.name}</span>
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-semibold text-primary">{formatCurrency(cost.totalCost)}</span>
                  <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Tooltip delayDuration={0}>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={() => { setEditingCost(cost); setAddOpen(true); }}
                          className="p-1 text-muted-foreground hover:text-foreground rounded"
                        >
                          <Edit className="w-3 h-3" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>Edit</TooltipContent>
                    </Tooltip>
                    <Tooltip delayDuration={0}>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(cost)}
                          className="p-1 text-destructive/70 hover:text-destructive rounded"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>Delete</TooltipContent>
                    </Tooltip>
                  </div>
                </div>
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {cost.quantity} {cost.unit || "ea"} @ {formatCurrency(cost.unitCost)}
                {cost.supplier && <span className="ml-2 px-1 py-0.5 bg-muted rounded border border-border text-xs">Via: {cost.supplier}</span>}
              </div>
              {cost.notes && <div className="text-xs italic text-muted-foreground/70 mt-0.5">{cost.notes}</div>}
            </div>
          ))}
          {totalCost > 0 && (
            <div className="flex justify-between items-center px-2.5 py-2 text-xs font-semibold bg-muted/30">
              <span>Total Default Cost</span>
              <span>{formatCurrency(totalCost)}</span>
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Default Cost Dialog */}
      <Dialog open={addOpen} onOpenChange={(open) => { setAddOpen(open); if (!open) setEditingCost(null); }}>
        <DialogContent className="sm:max-w-md">
          <DefaultCostForm
            productId={productId}
            cost={editingCost}
            onSuccess={() => { setAddOpen(false); setEditingCost(null); }}
            onCancel={() => { setAddOpen(false); setEditingCost(null); }}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove this default cost from the product template. Existing line items won't be changed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTarget && onDeleteCost(deleteTarget.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function DefaultCostForm({ productId, cost, onSuccess, onCancel }: {
  productId: number;
  cost: ProductDefaultCost | null;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { formatCurrency } = useSettingsFormat();
  const isEditing = !!cost;
  const [catalogSearch, setCatalogSearch] = useState("");

  const { data: catalog } = useListInternalCatalog();
  const createCost = useCreateProductDefaultCost();
  const updateCost = useUpdateProductDefaultCost();

  const filteredCatalog = catalog?.filter(item =>
    !catalogSearch || item.name.toLowerCase().includes(catalogSearch.toLowerCase())
  );

  const form = useForm<DefaultCostForm>({
    resolver: zodResolver(defaultCostSchema),
    defaultValues: {
      catalogItemId: cost?.catalogItemId ?? null,
      name: cost?.name ?? "",
      quantity: cost?.quantity ?? 1,
      unit: cost?.unit ?? "",
      unitCost: cost?.unitCost ?? 0,
      supplier: cost?.supplier ?? "",
      notes: cost?.notes ?? "",
    }
  });

  const selectedCatalogId = form.watch("catalogItemId");
  const selectedCatalogItem = catalog?.find(c => c.id === Number(selectedCatalogId));

  const applyCatalogItem = (item: { id: number; name: string; unitCost: number; unit?: string | null }) => {
    form.setValue("catalogItemId", item.id);
    form.setValue("name", item.name);
    form.setValue("unitCost", Number(item.unitCost));
    form.setValue("unit", item.unit ?? "");
    setCatalogSearch("");
  };

  const clearCatalogItem = () => {
    form.setValue("catalogItemId", null);
  };

  const onSubmit = (data: DefaultCostForm) => {
    const payload = {
      catalogItemId: data.catalogItemId ?? null,
      name: data.name,
      quantity: data.quantity,
      unitCost: data.unitCost,
      unit: data.unit || null,
      supplier: data.supplier || null,
      notes: data.notes || null,
    };

    if (isEditing && cost) {
      updateCost.mutate({ productId, costId: cost.id, data: payload }, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListProductDefaultCostsQueryKey(productId) });
          toast({ title: "Default cost updated" });
          onSuccess();
        },
        onError: () => toast({ title: "Failed to update", variant: "destructive" }),
      });
    } else {
      createCost.mutate({ productId, data: payload }, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListProductDefaultCostsQueryKey(productId) });
          toast({ title: "Default cost added" });
          onSuccess();
        },
        onError: () => toast({ title: "Failed to add cost", variant: "destructive" }),
      });
    }
  };

  const isPending = createCost.isPending || updateCost.isPending;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit Default Cost" : "Add Default Cost"}</DialogTitle>
        </DialogHeader>

        {/* Catalog picker */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Cost Catalog (optional)</label>
          {selectedCatalogItem ? (
            <div className="flex items-center justify-between p-3 rounded-md border border-primary/30 bg-primary/5">
              <div>
                <p className="text-sm font-medium">{selectedCatalogItem.name}</p>
                <p className="text-xs text-muted-foreground">{formatCurrency(Number(selectedCatalogItem.unitCost))} per {selectedCatalogItem.unit || "each"}</p>
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={clearCatalogItem}>Change</Button>
            </div>
          ) : (
            <div className="space-y-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <Input
                  placeholder="Search catalog or fill in manually..."
                  value={catalogSearch}
                  onChange={e => setCatalogSearch(e.target.value)}
                  className="pl-8 text-sm"
                />
              </div>
              {catalogSearch && filteredCatalog !== undefined && filteredCatalog.length > 0 && (
                <div className="border rounded-md shadow-sm bg-card overflow-hidden max-h-36 overflow-y-auto">
                  {filteredCatalog.map(c => (
                    <button key={c.id} type="button"
                      className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-muted/60 transition-colors border-b last:border-0"
                      onClick={() => applyCatalogItem(c)}>
                      <div>
                        <p className="text-sm font-medium">{c.name}</p>
                        {c.description && <p className="text-xs text-muted-foreground truncate max-w-[200px]">{c.description}</p>}
                      </div>
                      <span className="text-sm font-semibold text-primary shrink-0 ml-2">{formatCurrency(Number(c.unitCost))}/{c.unit || "ea"}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <FormField control={form.control} name="name" render={({ field }) => (
          <FormItem><FormLabel>Item Name *</FormLabel><FormControl><Input placeholder="e.g. Labor, Material" {...field} /></FormControl><FormMessage /></FormItem>
        )} />

        <div className="grid grid-cols-3 gap-3">
          <FormField control={form.control} name="quantity" render={({ field }) => (
            <FormItem><FormLabel>Qty</FormLabel><FormControl><Input type="number" step="any" min="0.001" {...field} /></FormControl><FormMessage /></FormItem>
          )} />
          <FormField control={form.control} name="unit" render={({ field }) => (
            <FormItem><FormLabel>Unit</FormLabel><FormControl><Input placeholder="hrs, sqft" {...field} /></FormControl><FormMessage /></FormItem>
          )} />
          <FormField control={form.control} name="unitCost" render={({ field }) => (
            <FormItem><FormLabel>Cost ($)</FormLabel><FormControl><Input type="number" step="0.01" min="0" {...field} /></FormControl><FormMessage /></FormItem>
          )} />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <FormField control={form.control} name="supplier" render={({ field }) => (
            <FormItem><FormLabel>Supplier/Vendor</FormLabel><FormControl><Input placeholder="Optional" {...field} /></FormControl><FormMessage /></FormItem>
          )} />
          <FormField control={form.control} name="notes" render={({ field }) => (
            <FormItem><FormLabel>Notes</FormLabel><FormControl><Input placeholder="Optional" {...field} /></FormControl><FormMessage /></FormItem>
          )} />
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
          <Button type="submit" disabled={isPending}>
            {isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : isEditing ? "Save Changes" : "Add Cost"}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
